import { Component, HostListener } from '@angular/core';
import { RoleDataPage, RoleResponse } from '../../../modules/shared/models/general.modal';
import { MdaListResponse, MdaOption } from '../../../modules/services/utility-service.models';
import { FilterDrawerFieldConfig, FilterDrawerValue } from '../../../components/filter-drawer/filter-drawer.models';
import { ApiServiceService } from '../../../modules/services/api-service.service';
import { FilterDrawerComponent } from "../../../components/filter-drawer/filter-drawer.component";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RoleCreationModalComponent } from "../role-creation-modal/role-creation-modal.component";
import { BsModalService } from 'ngx-bootstrap/modal';
import { HttpErrorResponse } from '@angular/common/http';
import alertify from 'alertifyjs';
import { DropdownMenu } from "../../../components/dropdown-menu/dropdown-menu.component";
import { UnderscorePipe } from '../../../modules/pipes/underscore-pipe.pipe';


type RoleSearchFilter = {
  roleName: string;
  fromDate: string;
  toDate: string;
};

@Component({
  selector: 'app-role-landing',
  standalone: true,
  imports: [
    FilterDrawerComponent,
    CommonModule,
    FormsModule, 
    RoleCreationModalComponent, 
    DropdownMenu,
    UnderscorePipe
  ],
  templateUrl: './role-landing.component.html',
  styleUrl: './role-landing.component.scss'
})
export class RoleLandingComponent {
onToggleStatus(wf: any): void {
    this.openMenuIndex = null;
    console.log('Toggle workflow status:', wf.id);
  }
  isModalOpen = false;
  isViewModalOpen = false;
  isFilterDrawerOpen = false;
  activeTab = 'workflow';
  openMenuIndex: number | null = null;
  roleList!: RoleResponse;
  mdaOptions: MdaOption[] = [];
  mode: "CREATE" | "UPDATE" | "VIEW" = "CREATE";
  selectedRoleId: string;
  isLoadingRoles: boolean;

  readonly pageSize = 50;
  readonly emptyWorkflowFilters: RoleSearchFilter = {
    roleName: '',
    fromDate: '',
    toDate: '',
  };

  tabs = [
    { key: 'workflow', label: 'Workflow' },
    { key: 'users', label: 'User Management' },
    { key: 'roles', label: 'Role Management' },
  ];

  columns = [
    'Role Name',
    'Description',
    'Date Created',
    'Date Updated',
    'Status',
    'Actions',
  ];
  workflowFilters: RoleSearchFilter = { ...this.emptyWorkflowFilters };
  workflowFilterFields: FilterDrawerFieldConfig[] = [];

  ngOnInit(): void {
    this.buildWorkflowFilterFields();
    this.getMdas();
    this.fetchRoless();
  }
  constructor(private apiService: ApiServiceService,
      private modalService: BsModalService,) {}

  get activeFilterCount(): number {
    return Object.values(this.workflowFilters).filter((value) => value.trim().length > 0)
      .length;
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    this.openMenuIndex = null;
  }

  getTabClass(tab: string): string {
    if (tab === this.activeTab) {
      return 'bg-teal-600 text-white shadow-sm';
    }
    return 'text-slate-500 hover:text-slate-700 hover:bg-slate-100';
  }

  getStatusClass(status: string): string {
    const normalizedStatus = status.toUpperCase();

    if (normalizedStatus === 'ACTIVE') {
      return 'bg-emerald-50 text-emerald-700 border !border-emerald-200';
    }

    if (normalizedStatus === 'INACTIVE') {
      return 'bg-red-100 text-red-500 border !border-red-200';
    }

    return 'bg-slate-100 text-slate-500 border !border-slate-200';
  }

  fetchRoless(page = 1): void {
    const data = {
      ...this.buildWorkflowSearchPayload(),
      page,
      pageSize: this.pageSize,
    };

    this.isLoadingRoles = true;
    this.openMenuIndex = null;

    this.apiService.post('/roles/search', data).subscribe({
      next: (res: RoleResponse) => {
        this.roleList = res;
        this.isLoadingRoles = false;
      },
      error: (err) => {
        if (err instanceof HttpErrorResponse) {
          alertify.error(err.message || "Failed to fetch roles");
        } else {
          alertify.error(JSON.stringify(err) || "Failed to fetch roles");
        }
        console.error('Search failed', err);
        this.isLoadingRoles = false;
      },
    });
  }

  toggleMenu(index: number): void {
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  onCreateRole(): void {
    this.mode = "CREATE";
    this.isModalOpen = true;
  }

  onEdit(wf: any): void {
    console.log('Edit workflow:', wf.id);
    this.openMenuIndex = null;
  }

  onDeleteRole(roleId: string): void {
    this.apiService.delete(`/roles/${roleId}`).subscribe(
      {
        next: (response) => {
          this.fetchRoless();
          alertify.success("Role deleted successfully");
        },
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            alertify.error(error.message || 'Failed to delete role');
          } else {
            alertify.error(JSON.stringify(error) || 'Failed to delete role');
          }
        }
      }
    )
  }

  openFilters(): void {
    this.openMenuIndex = null;
    this.isFilterDrawerOpen = true;
  }

  closeFilters(): void {
    this.isFilterDrawerOpen = false;
  }

  applyFilters(rawFilters: FilterDrawerValue): void {
    this.workflowFilters = {
      ...this.emptyWorkflowFilters,
      ...Object.fromEntries(
        Object.entries(rawFilters).map(([key, value]) => [key, `${value ?? ''}`]),
      ),
    };

    this.isFilterDrawerOpen = false;
    this.fetchRoless(1);
  }

  getMdas(): void {
    this.apiService.get('/mdas').subscribe({
      next: (response: MdaListResponse) => {
        this.mdaOptions = response.data ?? [];
        this.buildWorkflowFilterFields();
      },
      error: () => {
        this.mdaOptions = [];
        this.buildWorkflowFilterFields();
      },
    });
  }

  private buildWorkflowSearchPayload(): RoleSearchFilter {
    return {
      roleName: this.workflowFilters.roleName.trim(),
      fromDate: this.workflowFilters.fromDate.trim(),
      toDate: this.workflowFilters.toDate.trim(),
    };
  }

  private buildWorkflowFilterFields(): void {
    this.workflowFilterFields = [
      {
        key: 'roleName',
        label: 'Role Name',
        type: 'text',
        placeholder: 'Filter by Role',
      },
      {
        key: 'fromDate',
        label: 'From Date',
        type: 'date',
      },
      {
        key: 'toDate',
        label: 'To Date',
        type: 'date',
      },
    ];
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.openMenuIndex = null;
    this.closeFilters();
  }

 rawPermissions = [ ];



onRoleSaved(event: any) {
  if (event && event.permissionRows) {

    const formattedPermissions = event.permissionRows.flatMap((item: any) => {
      const moduleName = item.module.replace(/[^a-zA-Z0-9]/g, '_');

      return Object.entries(item.permissions)
        .filter(([_, hasPermission]) => hasPermission === true)
        .map(([action]) => `CAN_${action.toUpperCase()}_${moduleName}`);
    });
    const payload = {
      name: event.roleName,
      status: event.status,
      description: event.description,
      permissions: formattedPermissions // sending the strings instead of the grid
    };

    if (this.mode === "CREATE") {
      this.apiService.post('/roles', payload).subscribe({
        next: (response: any) => {
          this.fetchRoless();
          alertify.success("Role created successfully");
        },
        error: (error: any) => {
          if (error instanceof HttpErrorResponse) {
            alertify.error(error.message || "Failed to update role");
          } else {
            alertify.error(JSON.stringify(error) || "Failed to update role");
          }
          console.error('Failed to update role:', error);
        }
      });
    } else if (this.mode === "UPDATE") {
      this.apiService.patch(`/roles/${this.selectedRoleId}`, payload).subscribe({
        next: (response: any) => {
          this.fetchRoless();
          alertify.success("Role updated successfully");
        },
        error: (error: any) => {
          if (error instanceof HttpErrorResponse) {
            alertify.error(error.message || "Failed to update role");
          } else {
            alertify.error(JSON.stringify(error) || "Failed to update role");
          }
          console.error('Failed to update role:', error);
        }
      });
    }
  }

  this.isModalOpen = false;
  this.fetchRoless();
}

  onViewRole(roleId: string) {
    this.selectedRoleId = roleId;
    this.mode = "VIEW";
    this.isModalOpen = true;
  }

  onEditRole(roleId: string) {
    this.selectedRoleId = roleId;
    this.mode = "UPDATE";
    this.isModalOpen = true;
  }

  mapPermissionsToGrid(permissionsFromApi: any[]) {
    // 1. Extract just the 'name' strings from the objects
    const flatPermissionNames = permissionsFromApi.map(p => p.name);

    // 2. Define your system modules (the "pretty" names)
    const modules = [
      "Workflow Management", "Vendor Management", "Payments Management",
      "Assessments Management", "Reports & Analytics", "User Management", "Role Management"
    ];

    // 3. Reconstruct the grid
    const permissionRows = modules.map(moduleTitle => {
      // Sanitize title to match the "CAN_..._Module_Name" format
      // Note: Use /[^a-zA-Z0-9]/g to match the "Reports___Analytics" triple underscore
      const sanitizedModule = moduleTitle.replace(/[^a-zA-Z0-9]/g, '_');

      return {
        module: moduleTitle,
        permissions: {
          view: flatPermissionNames.includes(`CAN_VIEW_${sanitizedModule}`),
          create: flatPermissionNames.includes(`CAN_CREATE_${sanitizedModule}`),
          edit: flatPermissionNames.includes(`CAN_EDIT_${sanitizedModule}`),
          approve: flatPermissionNames.includes(`CAN_APPROVE_${sanitizedModule}`),
          delete: flatPermissionNames.includes(`CAN_DELETE_${sanitizedModule}`)
        }
      };
    });
    return permissionRows;
  }
}
